import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import BlockingStatusOverlay from './ui/BlockingStatusOverlay';
import {
  LIVE_COACHING_SOCKET_NAMES,
  LIVE_RECONNECT_COPY,
  RECONNECT_OVERLAY_GRACE_MS,
} from '../constants/liveReconnect';
import { WATCHER_COPY } from '../constants/watchSession';

interface LiveReconnectOverlayProps {
  /** A call is live on this page: a bot call (owned or watched) or a watched desktop call. */
  isSessionActive: boolean;
  /** The signed-in user runs this call (added the bot): only they may end it. */
  isSessionOwner: boolean;
  /**
   * The End flow's own modal (MeetBotModal, opened by Remove Bot or by this
   * overlay's End session) is open. It blocks the page itself, so the overlay
   * steps aside and the rep can finish or cancel; cancelled, the overlay is back.
   */
  isEndFlowOpen: boolean;
  /** The owner's normal End flow: the Remove Bot modal and its confirmation. */
  onEndSession: () => void;
  /** A watcher's way out: close its sockets and return to the start screen. */
  onLeave: () => void;
  /** The way-out button's label: "Leave" for a watcher, "Leave call" for a co-seller (desktop parity). */
  leaveLabel?: string;
}

/** True once `value` has stayed true for `durationMs`; false again the moment it is not. */
function useHeldTrueFor(value: boolean, durationMs: number): boolean {
  const [hasHeld, setHasHeld] = useState(false);

  useEffect(() => {
    if (!value) {
      setHasHeld(false);
      return undefined;
    }
    const timer = setTimeout(() => setHasHeld(true), durationMs);
    return () => clearTimeout(timer);
  }, [value, durationMs]);

  return value && hasHeld;
}

/**
 * Blocks the live view with "Reconnecting to live coaching…" once a coaching
 * socket (recall, production, landmine, competitor, standalone) has been
 * retrying for RECONNECT_OVERLAY_GRACE_MS during a session; clears the moment
 * every one is back. services/liveReconnect.ts reports the sockets to
 * connectionSlice. Same overlay and copy as macOS and WPF.
 *
 * One action, so nobody is trapped behind it: the call's owner gets End
 * session (the page's own End flow); a watcher, of someone else's bot or of a
 * desktop call, gets Leave (owner decision 2026-10-03), which only closes the
 * watcher's own sockets.
 */
const LiveReconnectOverlay: React.FC<LiveReconnectOverlayProps> = ({
  isSessionActive,
  isSessionOwner,
  isEndFlowOpen,
  onEndSession,
  onLeave,
  leaveLabel = WATCHER_COPY.leave,
}) => {
  const isCoachingSocketReconnecting = useSelector((state: RootState) =>
    (state.connection?.reconnectingSockets ?? []).some((socket) => LIVE_COACHING_SOCKET_NAMES.includes(socket))
  );
  const hasDropOutlastedGrace = useHeldTrueFor(isCoachingSocketReconnecting, RECONNECT_OVERLAY_GRACE_MS);

  const action = isSessionOwner
    ? { label: LIVE_RECONNECT_COPY.endSession, onClick: onEndSession }
    : { label: leaveLabel, onClick: onLeave };

  return (
    <BlockingStatusOverlay
      isOpen={isSessionActive && hasDropOutlastedGrace && !isEndFlowOpen}
      title={LIVE_RECONNECT_COPY.reconnecting}
      subtitle={LIVE_RECONNECT_COPY.reconnectingDetail}
      action={action}
    />
  );
};

export default LiveReconnectOverlay;
